import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as procService from "../../services/procService";
import { useModule } from "../Platform/ModuleGate";
import { hasModulePermission } from "../../constants/platformModules";
import { DataTable, ProcBadge, amount, csvStamp, downloadCsv, rfqKey } from "./procUi";
import { AppDropdown, EmptyState, LoadState, PageHeader, PrimaryButton, SecondaryButton, formatDate, inputStyle, pageStyle, useLoad } from "../../components/ui";

// RFQs (buyer side): every request for quotation, newest first.
const FILTERS = [
  { label: "All RFQs", value: null },
  { label: "Draft", value: "0" },
  { label: "Live", value: "1" },
  { label: "Under evaluation", value: "13" },
  { label: "Awarded", value: "3" },
  { label: "Closed", value: "12" },
];

export default function Rfqs() {
  const navigate = useNavigate();
  const { access } = useModule();
  const canManage = hasModulePermission(access, "PROC", "PROC_ADMIN", "PROC_BUYER");
  const [status, setStatus] = useState(null);
  const [typed, setTyped] = useState("");
  const [q, setQ] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setQ(typed.trim()), 300);
    return () => clearTimeout(t);
  }, [typed]);

  const { data, loading, error, reload } = useLoad(() => procService.listRfqs({ status, q }), [status, q]);
  const rows = data || [];
  const filtered = !!(q || status);
  const exportCsv = () =>
    downloadCsv(`rfqs-${csvStamp()}.csv`, ["RFQ", "Title", "Status", "Award type", "Deadline", "Delivery date", "Suppliers", "Quotations", "Lowest quotation", "Currency", "Created by"], rows.map((r) => [r.rfq_number || "", r.title, r.status_label, r.rfq_type, r.responsible_date || "", r.delivery_date || "", r.supplier_count, r.quotation_count, r.lowest_total ?? "", r.currency, r.created_by_name || ""]));

  return (
    <div style={pageStyle}>
      <PageHeader
        title="Requests for quotation"
        subtitle="Ask your suppliers to quote, compare what they send and choose"
        actions={<span className="d-flex gap-2 flex-wrap"><SecondaryButton onClick={exportCsv} disabled={!rows.length}>Export CSV</SecondaryButton>{canManage ? <PrimaryButton onClick={() => navigate("/proc/rfqs/new")}>New RFQ</PrimaryButton> : null}</span>}
      />
      <div className="d-flex flex-wrap gap-2 mb-3">
        <input className="form-control" style={{ ...inputStyle, maxWidth: 320 }} placeholder="Search RFQs" aria-label="Search RFQs" value={typed} onChange={(e) => setTyped(e.target.value)} />
        <div style={{ minWidth: 200 }}>
          <AppDropdown id="proc-rfq-status" value={status} options={FILTERS} onChange={(e) => setStatus(e.value ?? null)} aria-label="Status" />
        </div>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={rows}
          onRowClick={(r) => navigate(`/proc/rfqs/${r.id}`)}
          empty={
            <EmptyState
              title={filtered ? "No RFQs match" : "No RFQs yet"}
              text={filtered ? "Try another search or status." : "Create an RFQ, choose the suppliers and send it. Their quotations come back here for you to compare."}
              action={canManage && !filtered ? <PrimaryButton onClick={() => navigate("/proc/rfqs/new")}>New RFQ</PrimaryButton> : null}
            />
          }
          columns={[
            {
              label: "RFQ",
              render: (r) => (
                <>
                  <div style={{ fontWeight: 600, color: "#111827" }}>{r.title}</div>
                  <div style={{ fontSize: 12, color: "#6B7280" }}>{r.rfq_number || "Not sent yet"}</div>
                </>
              ),
            },
            { label: "Status", render: (r) => <ProcBadge status={rfqKey(r)} label={r.status_label} /> },
            { label: "Deadline", render: (r) => (r.responsible_date ? formatDate(r.responsible_date) : "—") },
            { label: "Quotations", render: (r) => (r.status === 0 ? "—" : `${r.quotation_count} of ${r.supplier_count}`) },
            { label: "Lowest", render: (r) => (r.lowest_total === null || r.lowest_total === undefined ? "—" : `${r.currency} ${amount(r.lowest_total)}`), style: { whiteSpace: "nowrap" } },
            { label: "Lines", render: (r) => r.line_count },
            { label: "Created by", render: (r) => r.created_by_name },
          ]}
        />
      </LoadState>
    </div>
  );
}
